/**
 * Sacrasoul, Email Capture Popup — behaviour
 *
 * Moved out of snippets/popup-email.liquid (2026-09-10, perf/oils-resins-speed)
 * where it shipped as a 45,544-byte inline <script> on every non-cart page:
 * inline script is uncacheable (re-downloaded with every HTML response) and
 * blocks the parser, even though nothing in it runs before a 20-45s delay
 * (see MIN_DELAY_MS / MAX_DELAY_MS below). As a cached, deferred external
 * asset it is fetched once per visitor and never re-sent on navigation.
 *
 * Locale- and route-dependent values (translated strings, the cart JSON URL,
 * the active locale) cannot live in a static asset, so they are handed in by
 * snippets/popup-email.liquid through one small inline
 * <script>window.SacrasoulPopupConfig = {...}</script> block rendered ahead
 * of this file. See CFG below. Everything else — DOM ids, timing, Klaviyo
 * calls, consent gating — is unchanged from the original inline script.
 */
(function() {
  'use strict';

  // ---- CONFIG ----
  var KLAVIYO_COMPANY_ID = 'VFiMuh';
  var KLAVIYO_LIST_ID    = 'UGtiNP';
  var IS_MOBILE        = window.innerWidth <= 640;
  /* The politeness stack built through July priced the courtesy too high:
     over half of sessions end before a 35-45s floor, and only 8-14% of all
     sessions ever met the popup (PostHog, 28 Jul - 01 Aug); list joins fell
     from ~11/day to ~2/day the week the stack landed and never recovered.
     The floor returns to the pre-collapse 20/25s, and the ceiling below is a
     promise, not a re-check: past it the offer shows unless a hard block
     (open drawer, typing, another dialog, hidden tab) is up right then. */
  var MIN_DELAY_MS     = IS_MOBILE ? 25000 : 20000;  // quiet floor: nothing appears before this
  var MAX_DELAY_MS     = 45000;                      // hard ceiling: the offer is owed from here
  /* A pause worth answering is a few quiet seconds, not an abandoned tab.
     15-20s of total stillness almost never happens in a live session, which
     is why the offer stopped arriving at all. */
  var ACTIVITY_IDLE_MS = IS_MOBILE ? 8000 : 6000;      // no interruption while the visitor is acting
  var FORM_IDLE_MS     = 30000;                        // a form stays protected through thinking pauses
  var EXIT_MIN_MS      = 8000;                         // earliest an exit signal may be answered
  var AWAY_MIN_MS      = 3000;                         // a real tab-away, not a stray blur
  var AWAY_QUIET_MS    = 1500;                         // someone who left and came back has already paused
  var ENGAGED_SCROLL   = 0.55;                         // read past this and the interest is real
  var CONSENT_GRACE_MS = 3000;                         // consent answer is an action; let the next task settle
  var COOKIE_NAME      = 'sacrasoul_popup';
  var WELCOME_CODE_KEY = 'ss_welcome_code';
  var WELCOME_CODE     = 'SACRA10';
  var COOKIE_DAYS      = 30;
  var COOKIE_DAYS_SUBMITTED = 365;
  var AUTO_CLOSE_MS    = 12000;

  // Translated strings for JS-driven text changes
  // Values that vary by locale/route come from window.SacrasoulPopupConfig,
  // set by one small inline <script> in snippets/popup-email.liquid.
  var CFG = window.SacrasoulPopupConfig || {};
  var T_CTA         = CFG.tCta;
  var T_CTA_LOADING = CFG.tCtaLoading;
  var T_COPIED      = CFG.tCopied;
  var T_PASTE       = CFG.tPaste;
  var T_APPLIED     = CFG.tApplied;
  var T_SUCCESS_APPLIED = CFG.tSuccessApplied;
  var T_SUCCESS_READY = CFG.tSuccessReady;
  var T_SUCCESS_FALLBACK = CFG.tSuccessFallback;
  var CART_JSON_URL = CFG.cartJsonUrl;
  var LOCALE        = CFG.locale;

  // ---- ELEMENTS ----
  // The dialog ships inert in <template id="ssPopupTpl"> (no DOM, no paint,
  // no ghost through an async sheet). Mount it now: this file only runs after
  // `load`, and only when the offer can still be shown. A harness that
  // provides the elements directly is left alone.
  if (!document.getElementById('ssPopup')) {
    var tpl = document.getElementById('ssPopupTpl');
    if (tpl && tpl.content && document.body) document.body.appendChild(tpl.content.cloneNode(true));
  }
  var popup          = document.getElementById('ssPopup');
  var overlay        = document.getElementById('ssPopupOverlay');
  var closeBtn       = document.getElementById('ssCloseBtn');
  var dismissLink    = document.getElementById('ssDismissLink');
  var submitBtn      = document.getElementById('ssSubmitBtn');
  var emailInput     = document.getElementById('ssEmailInput');
  var formContent    = document.getElementById('ssFormContent');
  var successContent = document.getElementById('ssSuccessContent');
  var successBody    = document.getElementById('ssSuccessBody');
  var discountCode   = document.getElementById('ssDiscountCode');
  var copyHint       = document.getElementById('ssCopyHint');
  var errorMsg       = document.getElementById('ssErrorMsg');

  /* ---- COMMITTED FOR THE VISIT (slice: popup-commitment) ----
     Selectors describe one page's buy box. They cannot see a collection
     quick-add, which posts through .ssb-add inside .ssb-buy-form and matches
     neither list, and a page-local flag dies with the document. A real PL
     session added three items on /collections/all (12:24:02, 12:24:24,
     12:24:30), reloaded at 12:24:45, and still met the offer at 12:25:27 —
     57s after buying three things (PostHog 019fa840-7ba9-7461-abf5-78b06bdc150b).

     The authority is the confirmed add itself: sacrasoul:behavior /
     cart_item_added, the provider-neutral event every producer emits — the PDP
     form, the collection quick-add, the drawer, the upsell — so no new selector
     has to be maintained for each surface. sessionStorage carries the answer
     across a navigation or a same-path reload and dies with the tab, which is
     exactly the span of one visit.

     THIS BLOCK RUNS FIRST, and both reasons are load-bearing:

       1. The visit gate in the trigger logic calls committedThisVisit(). A
          function declaration hoists; a var ASSIGNMENT does not. Sitting below
          that gate, COMMITTED_KEY was still undefined when the gate ran, so it
          read sessionStorage.getItem(undefined), got null, and never fired.
          Declaring the key before the gate is the whole fix.
       2. The recorder has to outlive the offer. The cart page renders no dialog
          at all (see the unless around the markup) and the path gate returns
          before any timer arms — yet an add confirmed on either must still
          silence the offer on the pages that follow. So the listener is bound
          above every return in this file, including the missing-dialog one.

     Deliberately NOT widened to cart_opened: opening the cart to look at it is
     not buying, and the drawer-open cooldown further down already holds that
     case. Storage is wrapped both ways — this listener runs inside the cart
     engine's own dispatch, so it must never throw, and a blocked jar degrades
     to per-document memory rather than to no popup at all. */
  var COMMITTED_KEY  = 'ss_popup_committed_visit';
  /* The snippet's parse-time recorder may have caught an add before this file
     finished downloading. It leaves the moment behind so the drawer cooldown
     below measures from the real add, not from whenever the download landed. */
  var lastBuyTouchAt = window.__ssPopupCommittedAt || 0;

  function committedThisVisit() {
    try { return sessionStorage.getItem(COMMITTED_KEY) === '1'; } catch (e) { return false; }
  }

  /* Seeded from storage so every guard below stays correct on its own terms,
     without depending on where the trigger-logic gate sits. */
  var hasCommitted = committedThisVisit();

  function commitForVisit() {
    hasCommitted = true;
    lastBuyTouchAt = Date.now();
    try { sessionStorage.setItem(COMMITTED_KEY, '1'); } catch (e) {}
  }

  function onCartBehavior(event) {
    var detail = event && event.detail;
    if (!detail || detail.name !== 'cart_item_added') return;
    commitForVisit();
  }

  /* One recorder per document. The snippet renders once, but a duplicate
     include or a re-executed script must never stack listeners on the cart
     engine's own dispatch. */
  if (!window.__ssPopupCommitmentBound) {
    window.__ssPopupCommitmentBound = true;
    document.addEventListener('sacrasoul:behavior', onCartBehavior);
  }
  /* ---- end slice: popup-commitment ---- */

  /* No dialog in this document — the cart page and the theme editor render the
     recorder above and nothing else. Everything past here is the offer. */
  if (!popup) return;

  // ---- COOKIE HELPERS ----
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = name + '=' + value + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  // ---- ONE IMPRESSION PER VISIT ----
  /* The 30-day cookie is only written on close or submit, so someone who met
     the offer and simply moved on met it again on the next page: one real
     session took two impressions in 58s across home and /collections/all,
     another took six across three products (PostHog, 27-28 Jul). The page-local
     flag was the only guard and it dies with the page. The impression belongs
     to the visit, so remember it there. sessionStorage survives same-tab
     navigation and reload, and dies with the tab. Close (30 days) and submit
     (1 year) keep their own longer memory, untouched.
     If storage is blocked this degrades to the old per-page behavior rather
     than removing the popup: showing twice is a smaller failure than never
     capturing an email, and it must never throw on the trigger path. */
  var SHOWN_THIS_VISIT_KEY = 'ss_popup_shown_visit';

  function shownThisVisit() {
    try { return sessionStorage.getItem(SHOWN_THIS_VISIT_KEY) === '1'; } catch (e) { return false; }
  }

  function markShownThisVisit() {
    try { sessionStorage.setItem(SHOWN_THIS_VISIT_KEY, '1'); } catch (e) {}
  }

  // ---- CORE FUNCTIONS ----
  var readyTimer = null;
  var openedAt = 0;
  var triggerReason = '';
  var closedEmitted = false;

  /* Provider-neutral behavior events, same bridge the cart engine uses.
     Nothing about the popup's own numbers was measurable before this: the
     retimed floor and the exit-intent path cannot be judged without an
     impression count. Analytics failures must never affect the popup. */
  function emitPopup(name, properties) {
    try {
      if (window.SSBehavior && typeof window.SSBehavior.emit === 'function') {
        window.SSBehavior.emit(name, properties || {});
        return;
      }
      document.dispatchEvent(new CustomEvent('sacrasoul:behavior', {
        detail: { name: name, properties: properties || {} }
      }));
    } catch (e) {}
  }

  function openPopup() {
    // the visual ships as data-src (see the img comment above); pay for its bytes
    // only when someone actually opens the dialog
    var visual = popup.querySelector('.sacrasoul-popup__visual-img[data-src]');
    if (visual) {
      visual.setAttribute('src', visual.getAttribute('data-src'));
      visual.removeAttribute('data-src');
    }
    popup.classList.remove('is-closing');
    popup.classList.add('active');
    overlay.classList.add('active');
    openedAt = Date.now();
    closedEmitted = false;
    emitPopup('popup_shown', {
      trigger: triggerReason || 'quiet',
      seconds_on_page: Math.round((Date.now() - startedAt) / 1000),
      engaged: hasEngaged(),
      is_mobile: IS_MOBILE,
      restored: wasRestored,
      cohort: paidProductVisit ? 'paid_product' : 'other',
      variant: offerVariant()
    });
    /* Taps only count once it has arrived and stopped moving. Before this the
       popup was already taking taps at opacity 0, so a tap aimed at the close
       cross landed on the page underneath and did nothing. */
    if (readyTimer) clearTimeout(readyTimer);
    readyTimer = setTimeout(function () {
      if (popup.classList.contains('active')) popup.classList.add('is-ready');
    }, 450);
    /* desktop only: on touch devices an autofocus summons the keyboard the
       instant the popup lands, shoving the layout mid-tap */
    if (window.matchMedia && !window.matchMedia('(hover: none)').matches) emailInput.focus();
  }

  function closePopup(method) {
    if (readyTimer) { clearTimeout(readyTimer); readyTimer = null; }
    if (popup.classList.contains('active') && !closedEmitted) {
      closedEmitted = true;
      emitPopup('popup_closed', {
        method: typeof method === 'string' ? method : 'other',
        seconds_open: openedAt ? Math.round((Date.now() - openedAt) / 1000) : null
      });
    }
    /* First tap wins: kill interactivity in the same frame so there is never a
       moment where the popup is still there and still taking taps. */
    popup.classList.add('is-closing');
    popup.classList.remove('is-ready');
    popup.classList.remove('active');
    overlay.classList.remove('active');
    if (!getCookie(COOKIE_NAME)) {
      setCookie(COOKIE_NAME, 'closed', COOKIE_DAYS);
    }
  }

  function rememberWelcomeCode() {
    try {
      localStorage.setItem(WELCOME_CODE_KEY, JSON.stringify({
        code: WELCOME_CODE,
        expiresAt: Date.now() + COOKIE_DAYS * 24 * 60 * 60 * 1000
      }));
    } catch (e) {}
    setCookie(WELCOME_CODE_KEY, WELCOME_CODE, COOKIE_DAYS);
  }

  function forgetWelcomeCode() {
    try { localStorage.removeItem(WELCOME_CODE_KEY); } catch (e) {}
    document.cookie = WELCOME_CODE_KEY + '=;Max-Age=0;path=/;SameSite=Lax';
  }

  function scheduleSuccessClose(delay) {
    setTimeout(function () { closePopup('auto_after_success'); }, delay || AUTO_CLOSE_MS);
  }

  function fetchWithTimeout(url, options, timeout) {
    var controller = window.AbortController ? new AbortController() : null;
    var requestOptions = options || {};
    if (controller) requestOptions.signal = controller.signal;
    var timer = controller ? setTimeout(function () { controller.abort(); }, timeout || 8000) : null;
    return fetch(url, requestOptions).then(function (response) {
      if (timer) clearTimeout(timer);
      return response;
    }, function (error) {
      if (timer) clearTimeout(timer);
      throw error;
    });
  }

  /* Klaviyo's endpoint is a third-party host, and for a few visitors it never
     answers: 4 of ~156 popup signups, 14-28 Sep 2026, failed before any
     response (status 0) on every retry, and no join reached Klaviyo. Three
     were Meta in-app browsers, one Mac Safari; PostHog reached us from the
     same pages, so it is their device or network dropping a.klaviyo.com.
     The storefront's own origin always answers. This is the footer's route:
     Shopify's newsletter form, which records the consent in Shopify; Klaviyo's
     Shopify sync carries it into "Email List" (a Welcome trigger). Proven
     live 2026-09-29: token in 0.7s, stored in 2.1s. Shopify redirects a stored
     signup to ?customer_posted=true; anything else is not a signup.
     Kept identical to the copy in assets/sacrasoul-cart-email.js (tested). */
  function subscribeViaShopify(email, tag) {
    return new Promise(function (resolve, reject) {
      var base = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/');
      var form = document.createElement('form');
      form.method = 'post';
      form.action = base + 'contact';
      form.hidden = true;
      [['form_type', 'customer'], ['utf8', '\u2713'], ['contact[tags]', 'newsletter,' + tag], ['contact[email]', email]].forEach(function (pair) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = pair[0];
        input.value = pair[1];
        form.appendChild(input);
      });
      document.body.appendChild(form);
      var settled = false;
      var timer = setTimeout(function () {
        var error = new Error('Shopify signup timed out');
        error.status = 0;
        done(error);
      }, 20000);
      function done(error) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (form.parentNode) form.parentNode.removeChild(form);
        if (error) reject(error); else resolve();
      }
      var sent = false;
      function send() {
        /* Shopify's page-level submit handler may call submit() again after
           the provider has; one signup is one post. */
        if (sent) return;
        sent = true;
        fetchWithTimeout(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' }, 15000)
          .then(function (response) {
            if (response.ok && /[?&]customer_posted=true/.test(response.url || '')) return done();
            var error = new Error('Shopify signup not stored');
            error.status = response.status;
            done(error);
          }, function (error) {
            var failure = new Error((error && error.message) || 'Shopify signup failed');
            failure.status = 0;
            done(failure);
          });
      }
      /* Shopify's bot protection answers a tokenless post with 400 "Missing
         CAPTCHA token". Its own script adds the token on submit and then calls
         the form's native submit(); this form's submit() sends that tokened
         form with fetch instead, so the page stays put and the answer can be
         read (a framed answer is refused: X-Frame-Options DENY). */
      form.submit = send;
      var captcha = window.Shopify && window.Shopify.captcha;
      if (captcha && typeof captcha.protect === 'function' && typeof form.requestSubmit === 'function') {
        try {
          captcha.protect(form, function () { form.requestSubmit(); });
        } catch (protectError) {
          send();
        }
      } else {
        send();
      }
    });
  }

  /* status 0 says nothing on its own; name the failure so the next one can
     be told apart (TypeError = never answered, AbortError = our timeout). */
  function describeFailure(error, startedAt) {
    return {
      error: String((error && error.name) || 'Error').slice(0, 40),
      message: String((error && error.message) || '').slice(0, 120),
      ms: Date.now() - startedAt,
      online: navigator.onLine !== false
    };
  }

  function cartHasWelcomeCode(cart) {
    if (!cart) return false;
    if ((cart.discount_codes || []).some(function(d) {
      return d && d.code && d.code.toUpperCase() === WELCOME_CODE && d.applicable !== false;
    })) return true;
    if ((cart.cart_level_discount_applications || []).some(function(d) {
      return d && d.title && d.title.toUpperCase() === WELCOME_CODE;
    })) return true;
    return (cart.items || []).some(function(item) {
      return (item.line_level_discount_allocations || []).some(function(a) {
        var application = a && a.discount_application;
        return application && application.title && application.title.toUpperCase() === WELCOME_CODE;
      });
    });
  }

  function markDiscountReady() {
    if (successBody) successBody.textContent = T_SUCCESS_READY;
    scheduleSuccessClose();
  }

  function markDiscountApplied() {
    forgetWelcomeCode();
    if (successBody) successBody.textContent = T_SUCCESS_APPLIED;
    if (copyHint) {
      copyHint.textContent = T_APPLIED;
      copyHint.style.opacity = '0.8';
    }
    if (discountCode) {
      discountCode.classList.add('is-applied');
      discountCode.removeAttribute('role');
      discountCode.removeAttribute('tabindex');
      discountCode.removeAttribute('aria-label');
    }
    document.dispatchEvent(new CustomEvent('sacrasoul:discount-applied', {
      detail: { code: WELCOME_CODE }
    }));
    scheduleSuccessClose();
  }

  function showDiscountFallback() {
    if (successBody) successBody.textContent = T_SUCCESS_FALLBACK;
    /* Keep the code available, but do not hold the page indefinitely after a
       network failure. A full minute is ample time to copy or close it. */
    scheduleSuccessClose(60000);
  }

  function showSuccess(via) {
    formContent.classList.add('hidden');
    successContent.classList.add('active');
    emitPopup('popup_submitted', {
      seconds_open: openedAt ? Math.round((Date.now() - openedAt) / 1000) : null,
      via: via || 'klaviyo'
    });
    setCookie(COOKIE_NAME, 'submitted', COOKIE_DAYS_SUBMITTED);
    rememberWelcomeCode();

    /* Shopify's discount route always redirects successfully, even for a code
       that cannot apply. Read the authoritative cart before making a money
       claim. An empty cart can only hold the code for later; a non-empty cart
       must show SACRA10 as applicable before we say it is applied. */
    /* Through the market root, like the cart ask: a bare /discount/ on
       /de-eu/ or /nl-eu/ leaves the shopper's market (the #2500 class). */
    fetchWithTimeout(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/') + 'discount/' + encodeURIComponent(WELCOME_CODE), {
      method: 'GET', credentials: 'same-origin'
    }, 8000)
      .then(function() {
        /* Shopify can return 422 from the discount route after still applying
           the code. Only cart.js is authoritative, so route status is ignored. */
        return fetchWithTimeout(CART_JSON_URL, { credentials: 'same-origin' }, 8000);
      })
      .then(function(response) {
        if (!response.ok) throw new Error('Cart verification failed');
        return response.json();
      })
      .then(function(cart) {
        if (Number(cart && cart.item_count || 0) === 0) {
          markDiscountReady();
          return;
        }
        if (!cartHasWelcomeCode(cart)) {
          forgetWelcomeCode();
          throw new Error('Discount not applicable');
        }
        markDiscountApplied();
      })
      .catch(showDiscountFallback);
  }

  // ---- KLAVIYO SUBSCRIBE ----
  function showError() {
    submitBtn.disabled = false;
    submitBtn.textContent = T_CTA;
    if (errorMsg) errorMsg.hidden = false;
    emailInput.focus();
  }

  function subscribeToKlaviyo(email) {
    submitBtn.disabled = true;
    submitBtn.textContent = T_CTA_LOADING;
    if (errorMsg) errorMsg.hidden = true;
    var startedAt = Date.now();

    /* Klaviyo unreachable or failing on its side: take the first-party
       route. A 4xx is Klaviyo judging the address, which Shopify would too. */
    function fallBack(failure) {
      subscribeViaShopify(email, 'popup').then(function () {
        emitPopup('popup_fallback', Object.assign({ ok: true }, failure));
        showSuccess('shopify');
      }, function (shopifyError) {
        emitPopup('popup_error', Object.assign({
          stage: 'klaviyo',
          fallback_status: (shopifyError && shopifyError.status) || 0
        }, failure));
        showError();
      });
    }

    fetchWithTimeout('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2026-07-15'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  /* Smart Translations reads native Locale; the Welcome split
                     still reads the custom language property below. */
                  locale: LOCALE,
                  properties: {
                    language: LOCALE
                  }
                }
              }
            },
            custom_source: 'Sacrasoul Welcome Popup'
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: KLAVIYO_LIST_ID
              }
            }
          }
        }
      })
    }, 15000)
    .then(function(response) {
      if (!response.ok) {
        console.warn('Klaviyo subscription failed with status', response.status);
        if (response.status === 429 || response.status >= 500) {
          fallBack({ status: response.status, error: 'http', message: '', ms: Date.now() - startedAt, online: true });
          return;
        }
        /* A failed POST used to leave no trace beyond this console line, so a
           broken submit path could hide inside "few impressions" forever. */
        emitPopup('popup_error', { stage: 'klaviyo', status: response.status });
        showError();
        return;
      }
      /* Cookie the browser to this email (assets/ss-klaviyo.js; internal visits skipped)
         so onsite Viewed Product events reach the browse-abandonment flow —
         the fetch above creates the profile server-side but never links
         this browser to it. */
      /* Identity is optional tracking: an SDK failure cannot undo an accepted
         signup or ask the visitor to submit the same address again. */
      try {
        if (window.SSKlv) window.SSKlv.identify(email);
      } catch (identityError) {
        console.warn('Klaviyo browser identification unavailable');
      }
      /* The signup is accepted; a fault past this point is ours to log,
         never a reason to resubmit or to tell the shopper it failed. */
      try { showSuccess(); } catch (successError) {
        console.warn('Popup success view failed:', successError);
      }
    }, function(error) {
      /* Only the request itself: a fault in the success path above must not
         send an accepted signup down the fallback a second time. */
      console.warn('Klaviyo subscription error:', error);
      fallBack(Object.assign({ status: 0 }, describeFailure(error, startedAt)));
    });
  }

  // ---- CLICK-TO-COPY ----
  if (discountCode) {
    discountCode.addEventListener('click', function() {
      if (discountCode.classList.contains('is-applied')) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(WELCOME_CODE).then(function() {
          discountCode.classList.add('copied');
          copyHint.textContent = T_COPIED + ' · ' + T_PASTE;
          copyHint.style.opacity = '0.8';
          scheduleSuccessClose(6000);
        });
      } else {
        var textArea = document.createElement('textarea');
        textArea.value = WELCOME_CODE;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        discountCode.classList.add('copied');
        copyHint.textContent = T_COPIED + ' · ' + T_PASTE;
        scheduleSuccessClose(6000);
      }
    });
    discountCode.addEventListener('keydown', function(e) {
      if ((e.key === 'Enter' || e.key === ' ') && !discountCode.classList.contains('is-applied')) {
        e.preventDefault();
        discountCode.click();
      }
    });
  }

  // ---- EVENT LISTENERS ----
  closeBtn.addEventListener('click', function () { closePopup('close_button'); });
  /* Arrival is not a dismissal. The dialog is pointer-events:none until the
     ready gate lifts (~350ms, measured), so for that whole window every tap
     aimed at the email field or the CTA fell through to the overlay and killed
     the offer, burning the 30-day suppression cookie with it. The shopper had
     not decided anything. The gate itself is right — it stops taps landing on
     a target that is still moving — but the overlay must not act until the
     thing it sits behind is ready to be aimed at. */
  overlay.addEventListener('click', function () {
    /* ...but a shopper must never be trapped. If the ready gate somehow never
       lifts -- the 450ms timer throttled away in a backgrounded tab AND the
       transition never ending -- ignoring the overlay forever would leave a
       phone with no way out at all, since there is no Escape key there. Past
       the window the arrival protects, the tap is a decision again. */
    if (!popup.classList.contains('is-ready') && Date.now() - openedAt < 1500) return;
    closePopup('overlay');
  });

  /* Belt and braces on the ready gate: if a throttled timer never fires (a
     backgrounded tab will do it), the arrival transition itself opens the
     popup for taps. It must never end up visible and untappable. */
  popup.addEventListener('transitionend', function (e) {
    if (e.target === popup && popup.classList.contains('active')) {
      popup.classList.add('is-ready');
    }
  });

  dismissLink.addEventListener('click', function(e) {
    e.preventDefault();
    closePopup('dismiss_link');
  });

  submitBtn.addEventListener('click', function() {
    var email = emailInput.value.trim();
    if (email && email.includes('@') && email.includes('.')) {
      subscribeToKlaviyo(email);
    } else {
      emailInput.style.borderColor = 'var(--ss-rust)';
      emailInput.focus();
      setTimeout(function() {
        emailInput.style.borderColor = 'rgba(190, 140, 115, 0.25)';
      }, 2000);
    }
  });

  emailInput.addEventListener('keypress', function(e) {
    if (e.key === 'Enter') submitBtn.click();
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && popup.classList.contains('active')) closePopup('escape');
  });

  // ---- TRIGGER LOGIC ----
  // Eligibility is not permission to interrupt. After the floor, every real
  // interaction restarts the quiet window. A focused field, recent form work,
  // another dialog, buying activity, or a hidden tab holds indefinitely.
  if (getCookie(COOKIE_NAME)) return;
  /* Already met this visit. Nothing left to ask until they close it or take it. */
  if (shownThisVisit()) return;
  /* Already buying. Once someone is buying the asking stops for the rest of the
     visit (owner rule), and that has to survive a navigation and a reload. Both
     the key and the reader are declared in COMMITTED FOR THE VISIT above, which
     is what makes this line do anything at all. */
  if (committedThisVisit()) return;

  var path = window.location.pathname;
  var localePath = path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
  /* Lead-capture and transaction surfaces. The live wholesale page is
     /pages/wholesale-partnerships; the bare handle is a 404, so it is named in
     full rather than relying on a prefix match that nobody would expect. */
  var suppressedPaths = ['/cart', '/checkout', '/pages/checkout', '/pages/the-circle',
                         '/pages/contact', '/pages/wholesale-partnerships',
                         '/pages/data-sharing-opt-out'];
  if (suppressedPaths.some(function(prefix) { return localePath.indexOf(prefix) === 0; })) return;

  var floorPassed = false;
  var ceilingReached = false;
  var hasTriggered = false;
  var consentWaitArmed = false;
  var consentPoll = null;
  var quietTimer = null;
  var lastActivityAt = Date.now();
  var lastFormActivityAt = 0;
  var startedAt = Date.now();
  var hiddenSince = 0;
  var engaged = false;
  var activityEvents = ['pointerdown', 'keydown', 'input', 'focusin', 'focusout'];

  /* Interest, not mere presence. A gift is worth offering to someone who came
     to look at something, read a long way down, came back for a second page,
     or opened the cart. Someone who has done none of that is still browsing,
     and the quiet floor alone decides for them. */
  // Paid product traffic keeps the reading and buying guards past the ceiling.
  // Carry the entry cohort across internal navigation; storage can be blocked.
  var paidProductVisit = false;
  try {
    var entry = new URLSearchParams(window.location.search);
    var medium = entry.get('utm_medium') || '';
    var paidEntry = /^(paid(?:_social)?|cpc|ppc|cpm|social_paid)$/i.test(medium)
      || entry.has('fbclid') || entry.has('gclid') || entry.has('ttclid');
    paidProductVisit = localePath.indexOf('/products/') === 0 && paidEntry;
    if (paidProductVisit) sessionStorage.setItem('ss_popup_paid_product_visit', '1');
    paidProductVisit = paidProductVisit || sessionStorage.getItem('ss_popup_paid_product_visit') === '1';
  } catch (e) {}
  /* The quieter offer, for everyone (7 Oct 2026, Sho). Past the ceiling a
     non-paid visit keeps the activity and buying guards that a paid visit
     always keeps, so the offer still waits for a real pause. It ran as the
     PostHog experiment popup-quiet-past-ceiling from 7 Sep: over 30 days and
     ~2,350 eligible visits an arm, reflex closes (x within 3 s) fell from
     18.1% to 13.4% of visits while sign-ups held (2.97% -> 2.77%, inside the
     noise) and checkouts held (4.09% -> 4.48%). It no longer depends on
     PostHog or consent: a visitor who declined analytics gets the same
     courtesy. quietVariant stays true so scheduleQuiet keeps the tested
     two-second recheck past the ceiling. */
  var QUIET_RECHECK_MS = 2000;
  var quietVariant = true;
  var quietTimerAt = 0;
  function quietPastCeiling() { return true; }
  function offerVariant() { return 'quiet'; }
  var paidSuppressionReported = false;
  function paidHeroReady() {
    var hero = document.querySelector('[data-r45-main], [data-ffr13-main], #gmain, .product-gallery img');
    // A decoded hero is stricter than a clock or a first (possibly text) LCP.
    return hero ? hero.complete && hero.naturalWidth > 0 : document.readyState === 'complete';
  }
  function paidOfferReady() {
    if (!paidProductVisit) return true;
    var reason = !paidHeroReady() ? 'hero_not_ready' : (!hasEngaged() ? 'not_engaged' : '');
    if (!reason) return true;
    if (!paidSuppressionReported) {
      paidSuppressionReported = true;
      emitPopup('popup_suppressed', { reason: reason, cohort: 'paid_product' });
    }
    return false;
  }
  function markEngaged() { engaged = true; }
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (target && target.closest && target.closest('details summary, [data-r45-thumb], [data-ffr13-thumb], .ritual-btn, [role="tab"]')) markEngaged();
  }, { passive: true });
  function hasEngaged() { return engaged || lastCartOpenSeen !== 0; }

  if (!paidProductVisit && (localePath.indexOf('/products/') === 0 || localePath.indexOf('/collections/') === 0)) markEngaged();
  try {
    var seenPages = Number(sessionStorage.getItem('ss_popup_pageviews') || 0) + 1;
    sessionStorage.setItem('ss_popup_pageviews', String(seenPages));
    if (seenPages >= 2) markEngaged();
  } catch (e) {}

  /* At the foot of the page there is nothing left to read. That is a real
     moment to themselves rather than a gap in the middle of a task, so it
     qualifies on a shorter stillness than a mid-page pause does. Without a
     signal like this, mobile has no exit intent and the offer would only ever
     arrive on a blind timer. */
  var AT_REST_SCROLL   = 0.92;
  var AT_REST_IDLE_MS  = 4000;
  var atRest = false;

  function noteScrollDepth() {
    try {
      var doc = document.documentElement;
      var reach = Math.max(1, doc.scrollHeight - window.innerHeight);
      var through = (window.scrollY || doc.scrollTop || 0) / reach;
      if (!engaged && through >= ENGAGED_SCROLL) markEngaged();
      atRest = through >= AT_REST_SCROLL;
    } catch (e) {}
  }

  function stillnessNeeded() {
    return atRest ? AT_REST_IDLE_MS : ACTIVITY_IDLE_MS;
  }

  function consentVisible() {
    var ids = ['shopify-pc__banner', 'shopify-pc__prefs'];
    for (var i = 0; i < ids.length; i++) {
      var el = document.getElementById(ids[i]);
      if (!el) continue;
      var cs = getComputedStyle(el);
      if (cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0') return true;
    }
    return false;
  }

  function noteActivity(event) {
    if (event && event.target && event.target.closest && event.target.closest('#ssPopup')) return;
    lastActivityAt = Date.now();
    if (event && event.target && event.target.closest && event.target.closest('form')) {
      lastFormActivityAt = lastActivityAt;
    }
    if (floorPassed) scheduleQuiet();
  }

  /* scrollHeight and scrollY read in the scroll event forced a style recalc + layout every frame (4 Oct, 390px / 4x CPU:
     485 to 582 ms per 10 s of scrolling on the Jade page, measured). The read waits for the task after the frame, when layout is clean. */
  var depthQueued = false;
  function lateDepth() {
    depthQueued = false;
    var was = atRest;
    noteScrollDepth();
    if (floorPassed && atRest !== was) scheduleQuiet();
  }
  function onScroll() {
    lastActivityAt = Date.now();
    if (!depthQueued) { depthQueued = true; setTimeout(lateDepth, 0); }
    if (floorPassed) scheduleQuiet();
  }

  activityEvents.forEach(function(name) {
    document.addEventListener(name, noteActivity, { passive: true, capture: true });
  });
  window.addEventListener('scroll', onScroll, { passive: true });

  function focusedField() {
    var active = document.activeElement;
    if (!active) return false;
    return /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName) || active.isContentEditable;
  }

  function otherDialogVisible() {
    var dialogs = document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"]');
    for (var i = 0; i < dialogs.length; i++) {
      var dialog = dialogs[i];
      if (dialog === popup || dialog.hasAttribute('inert')) continue;
      if (dialog.getAttribute('aria-hidden') === 'true') continue;
      var cs = getComputedStyle(dialog);
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
      var rect = dialog.getBoundingClientRect();
      if (!rect.width || !rect.height || rect.right <= 0 || rect.bottom <= 0
          || rect.left >= window.innerWidth || rect.top >= window.innerHeight) continue;
      return true;
    }
    return false;
  }

  function visitorIsBusy() {
    var now = Date.now();
    if (document.visibilityState !== 'visible') return true;
    if (focusedField()) return true;
    if (now - lastActivityAt < stillnessNeeded()) return true;
    if (lastFormActivityAt && now - lastFormActivityAt < FORM_IDLE_MS) return true;
    if (otherDialogVisible()) return true;
    return false;
  }

  function scheduleQuiet() {
    if (hasTriggered || hasCommitted) return;
    /* Past the ceiling the quiet variant is holding an offer that is otherwise
       owed, so it re-decides every two seconds, and activity must not push an
       armed recheck later: a consent withdrawal then reverts the visit to
       control within two seconds however fast the visitor scrolls. Stillness
       itself is still judged in attemptOpen, not by this cadence. Control and
       paid visits never enter this branch; their timer is untouched. */
    var variantPastCeiling = ceilingReached && !paidProductVisit && quietVariant === true;
    if (variantPastCeiling && document.visibilityState === 'visible'
        && quietTimer && quietTimerAt && quietTimerAt - Date.now() <= QUIET_RECHECK_MS) return;
    if (quietTimer) clearTimeout(quietTimer);
    if (document.visibilityState !== 'visible') { quietTimer = null; return; }
    var delay = Math.max(500, stillnessNeeded() - (Date.now() - lastActivityAt));
    if (variantPastCeiling) delay = Math.min(delay, QUIET_RECHECK_MS);
    quietTimer = setTimeout(attemptOpen, delay);
    quietTimerAt = Date.now() + delay;
  }

  function attemptOpen() {
    if (hasTriggered || !floorPassed) return;
    quietTimer = null;
    /* They are buying. Nothing else to decide, so stop asking this visit. */
    if (hasCommitted) { stopActivityWatch(); return; }
    if (consentVisible()) { armConsentWait(); return; }
    if (!paidOfferReady()) { scheduleQuiet(); return; }
    /* A paid product visit keeps the buying and activity guards past the
       ceiling. The quiet variant (flag, control by default) extends the same
       courtesy to a non-paid visit, and only past the ceiling: before it the
       flag is not even read, so PostHog's exposure lands on the visitors who
       reached the decision. Everyone else is owed the offer below. */
    var keepsGuards = paidProductVisit || (ceilingReached && quietPastCeiling());
    if (keepsGuards && isBuying()) { armBuyingWait(); return; }
    if (keepsGuards && visitorIsBusy()) { scheduleQuiet(); return; }
    if (!ceilingReached) {
      if (isBuying()) { armBuyingWait(); return; }
      if (visitorIsBusy()) { scheduleQuiet(); return; }
    } else {
      /* Past the ceiling the offer is owed. Only a hard block still holds:
         a hidden tab, an open drawer, a field being typed into, or another
         dialog on screen. Stillness and post-cart cooldowns no longer apply
         (they are what silenced the popup for five weeks). hasCommitted
         stays absolute above - never interrupt someone who added to cart. */
      /* armBuyingWait() would wait out the 60s post-drawer cooldown too, and
         scheduleQuiet() re-adds the stillness wait; past the ceiling only the
         block that is up RIGHT NOW may hold the offer, so every blocked path
         waits on the hard-block poll alone. */
      if (document.visibilityState !== 'visible'
          || isCartOrMenuOpen() || focusedField() || otherDialogVisible()) { armCeilingWait(); return; }
      triggerReason = triggerReason || 'ceiling';
    }
    fire();
  }

  function onVisibilityChange() {
    if (document.visibilityState !== 'visible') {
      hiddenSince = Date.now();
      return;
    }
    var awayFor = hiddenSince ? Date.now() - hiddenSince : 0;
    hiddenSince = 0;
    lastActivityAt = Date.now();
    /* Leaving and coming back is itself a pause. For someone already engaged,
       honour it instead of restarting the full quiet window from zero. */
    if (awayFor >= AWAY_MIN_MS && hasEngaged()) {
      lastActivityAt = Date.now() - Math.max(0, ACTIVITY_IDLE_MS - AWAY_QUIET_MS);
    }
    if (floorPassed) scheduleQuiet();
  }
  document.addEventListener('visibilitychange', onVisibilityChange);

  /* Intent that stopped. A pointer leaving through the top of the window is
     someone reaching for the tab bar, the back button, or the close button.
     They looked, they did not buy, and they are about to be gone. This is the
     one moment worth answering straight away, so it skips the stillness wait
     and the post-cart cooldown. It still never covers an open drawer, an open
     dialog, the consent banner, or a field being typed into. */
  function onExitIntent(event) {
    if (hasTriggered || hasCommitted) return;
    if (event.relatedTarget || event.toElement) return;
    if (event.clientY > 0) return;
    if (Date.now() - startedAt < EXIT_MIN_MS) return;
    if (!hasEngaged()) return;
    if (isBuying()) return;
    if (consentVisible() || isCartOrMenuOpen() || focusedField() || otherDialogVisible()) return;
    floorPassed = true;
    triggerReason = 'exit_intent';
    fire();
  }
  var exitIntentBound = false;
  try {
    if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', onExitIntent, true);
      exitIntentBound = true;
    }
  } catch (e) {}

  function stopActivityWatch() {
    activityEvents.forEach(function(name) {
      document.removeEventListener(name, noteActivity, { capture: true });
    });
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (exitIntentBound) document.removeEventListener('mouseout', onExitIntent, true);
  }

  /* One question, one answer: has this visitor already been asked (or already
     bought)? The trigger gates below read this at script init, but a page
     restored from the back-forward cache resumes its own timers with the
     page-local hasTriggered still false, while ANOTHER page in the same tab
     may have claimed the visit since. One US visitor met the popup twice 3.2s
     apart across a back-navigation (PostHog, 29-30 Jul) because fire() trusted
     init-time state. So the durable gates are re-read at the moment of truth,
     not only at load. */
  function alreadySatisfied() {
    return !!(getCookie(COOKIE_NAME) || shownThisVisit() || committedThisVisit() || hasCommitted);
  }

  /* Full disarm: no timer, watcher or poll survives, so a satisfied page can
     never re-ask. Shared by fire()'s stale-gate branch and the bfcache
     restore below. */
  function disarmAllTriggers() {
    if (quietTimer) { clearTimeout(quietTimer); quietTimer = null; }
    if (readyTimer) { clearTimeout(readyTimer); readyTimer = null; }
    if (buyingTimer) { clearInterval(buyingTimer); buyingTimer = null; }
    if (buyingWatch) { clearInterval(buyingWatch); buyingWatch = null; }
    if (ceilingWait) { clearInterval(ceilingWait); ceilingWait = null; }
    stopActivityWatch();
    if (consentPoll) { clearInterval(consentPoll); consentPoll = null; }
    document.removeEventListener('visitorConsentCollected', onConsentSettled);
  }

  /* bfcache restore: the browser hands back this page exactly as it was,
     armed timers included. If the visit was satisfied elsewhere in the
     meantime, stand down before any timer can fire. */
  var wasRestored = false;
  window.addEventListener('pageshow', function(e) {
    if (!e || !e.persisted) return;
    wasRestored = true;
    /* Re-seed the page-local commitment flag: the visitor may have added to
       cart on the page they are coming back from, and isBuying() reads the
       page-local, not storage. */
    hasCommitted = committedThisVisit();
    if (!hasTriggered && alreadySatisfied()) {
      hasTriggered = true;
      disarmAllTriggers();
    }
  });

  function fire() {
    if (hasTriggered) return;
    // Exit and return-to-tab paths must obey the same paid-entry contract.
    if (!paidOfferReady()) { scheduleQuiet(); return; }
    if ((paidProductVisit || (ceilingReached && quietPastCeiling())) && (visitorIsBusy() || isBuying())) { scheduleQuiet(); return; }
    /* Re-check the durable gates at the moment of firing. bfcache is the
       known path here, but any route to a stale page-local state ends the
       same way: the visit is already claimed, so this page stays quiet. */
    if (alreadySatisfied()) {
      hasTriggered = true;
      disarmAllTriggers();
      /* Positive proof the guard ran: a drop in popup_shown alone cannot
         distinguish "fixed" from "never triggered". NO cookie write here —
         suppression must not over-suppress a genuine later ask. */
      emitPopup('popup_suppressed', { reason: 'already_satisfied', restored: wasRestored });
      return;
    }
    hasTriggered = true;
    /* Claim the visit before the popup is exposed or anything is emitted, so a
       navigation that lands mid-animation still counts as the one impression. */
    markShownThisVisit();
    disarmAllTriggers();
    openPopup();
  }

  function armConsentWait() {
    if (consentWaitArmed) return;
    consentWaitArmed = true;
    document.addEventListener('visitorConsentCollected', onConsentSettled);
    consentPoll = setInterval(function () {
      if (!consentVisible()) onConsentSettled();
    }, 1500);
  }

  function onConsentSettled() {
    document.removeEventListener('visitorConsentCollected', onConsentSettled);
    if (consentPoll) { clearInterval(consentPoll); consentPoll = null; }
    consentWaitArmed = false;
    lastActivityAt = Date.now();
    /* The banner is the one gate allowed to outlive the ceiling, so its release
       has to hand back to the ceiling. scheduleQuiet() would hand back to the
       stillness wait instead, and every scroll and keypress re-arms that from
       zero: a visitor still reading in short bursts would never be offered at
       all. The banner is EU/UK, so that stranding would land on the PL market. */
    setTimeout(function () {
      if (ceilingReached) attemptOpen(); else scheduleQuiet();
    }, CONSENT_GRACE_MS);
  }

  // ---- BUYING AWARENESS ----
  // The offer must never interrupt a purchase (owner note 2026-07-24). It holds
  // while the cart or menu drawer is open, and for a cooldown after the cart was
  // last opened or closed (every add-to-cart opens the cart), so a shopper who is
  // actively buying is left in peace; the offer only lands in a browsing lull with
  // room for it, never on top of a buy action.
  var BUYING_COOLDOWN_MS = 60000;
  var lastCartOpenSeen   = 0;
  var buyingTimer        = null;

  /* Buying is not only an open cart. Choosing a size, changing a quantity,
     reaching for add-to-cart. All of it is someone in the middle of buying,
     and none of it was visible to the old check, which only watched the
     drawers. So a visitor picking a size got interrupted by the offer.
     Two grades:
       - touching the buy box  -> buying, on a cooldown
       - committing (add to cart / checkout) -> done, never ask again this visit */
  var BUY_BOX_SELECTOR = '#atc, #sacrasoul-checkout, [data-sticky-add], .add-to-cart-link,' +
                         ' .qty-wrapper, .qty-btn, .qty-input, .qty__btn,' +
                         ' .packs, .pack, [name="id"], [data-variant-id],' +
                         ' .cart-cta-sticky, .mobile-cta-btn';
  var COMMIT_SELECTOR  = '#atc, #sacrasoul-checkout, .add-to-cart-link, .mobile-cta-btn--cart';

  function noteBuyIntent(event) {
    var target = event && event.target;
    if (!target || !target.closest) return;
    if (target.closest('#ssPopup')) return;
    if (target.closest(COMMIT_SELECTOR)) {
      /* Reaching for add-to-cart. This suppresses from the tap, before the POST
         has answered, which is the half-second cart_item_added cannot cover.
         It stays page-local on purpose: an add that then fails must not silence
         the offer for the whole visit. Only a confirmed add does that. */
      hasCommitted = true;
      lastBuyTouchAt = Date.now();
      return;
    }
    if (target.closest(BUY_BOX_SELECTOR)) lastBuyTouchAt = Date.now();
  }
  document.addEventListener('pointerdown', noteBuyIntent, { passive: true, capture: true });
  document.addEventListener('change', noteBuyIntent, { passive: true, capture: true });

  // Query the drawers FRESH on every check. The cart drawer element is re-created
  // by the boot shell / cart engine after load, so a reference captured once would
  // go stale and stop seeing the open state. The IDs are stable; the elements are not.
  function isCartOrMenuOpen() {
    var c = document.getElementById('sacrasoul-cart-drawer');
    var m = document.querySelector('.ssd-root');
    return !!((c && c.classList.contains('is-open')) || (m && m.classList.contains('is-open')));
  }

  // A drawer being open is active buying. Refresh the "last open" moment once a
  // second while it stays open, so the cooldown runs from when the cart last closed.
  // A poll needs no coupling to the cart engine and survives the element re-creation.
  var buyingWatch = setInterval(function () { if (isCartOrMenuOpen()) lastCartOpenSeen = Date.now(); }, 1000);

  function isBuying() {
    if (hasCommitted) return true;
    if (isCartOrMenuOpen()) return true;
    if (lastBuyTouchAt !== 0 && (Date.now() - lastBuyTouchAt) < BUYING_COOLDOWN_MS) return true;
    return lastCartOpenSeen !== 0 && (Date.now() - lastCartOpenSeen) < BUYING_COOLDOWN_MS;
  }

  /* Past the ceiling: wait only for the current hard block to clear, never
     for cooldowns. Polls every 2s; hands back to attemptOpen(), whose ceiling
     branch re-checks the hard blocks and fires. */
  var ceilingWait = null;
  function armCeilingWait() {
    if (ceilingWait || hasTriggered) return;
    ceilingWait = setInterval(function () {
      if (hasTriggered || hasCommitted) { clearInterval(ceilingWait); ceilingWait = null; return; }
      if (document.visibilityState !== 'visible') return;
      if (isCartOrMenuOpen() || focusedField() || otherDialogVisible()) return;
      clearInterval(ceilingWait); ceilingWait = null;
      attemptOpen();
    }, 2000);
  }

  // While buying, poll quietly until it settles, then resume at the next pause.
  // Someone who has committed never settles back into "browsing", so stop watching.
  function armBuyingWait() {
    if (buyingTimer || hasCommitted) return;
    buyingTimer = setInterval(function () {
      if (hasCommitted) { clearInterval(buyingTimer); buyingTimer = null; return; }
      if (!isBuying()) { clearInterval(buyingTimer); buyingTimer = null; scheduleQuiet(); }
    }, 2000);
  }

  // Quiet floor: after it, look for the first genuine pause. The ceiling is
  // the guarantee: from there attemptOpen() drops the soft gates, so the offer
  // arrives at the first hard-block-free moment instead of never.
  setTimeout(function () {
    floorPassed = true;
    if (!hasTriggered && !hasCommitted) emitPopup('popup_eligible', { at: 'floor' });
    scheduleQuiet();
  }, MIN_DELAY_MS);
  setTimeout(function () {
    floorPassed = true;
    ceilingReached = true;
    attemptOpen();
  }, MAX_DELAY_MS);

})();
